import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, MapPin, Users, Calendar, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Job as MockJob } from '../../data/mockJobs';
import { Job as ApiJob } from '../../api/jobs';
import CompleteJobButton from './CompleteJobButton';
import { useLanguage } from '../../contexts/LanguageContext';

// Create a union type that handles both job formats
type Job = MockJob | ApiJob & {
  title: string;
  location: string;
  candidates: number;
  referencesNeeded: number;
  referencesCompleted: number;
  status: 'active' | 'paused' | 'completed' | 'ACTIVE' | 'COMPLETED';
  priority: 'overdue' | 'high' | 'low';
  dueDate: string;
};

export { Job };

interface JobCardProps {
  job: Job;
  onClick: (job: Job) => void;
  onJobCompleted?: (job: Job) => void;
}

const JobCard = ({ job, onClick, onJobCompleted }: JobCardProps) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  // Calculate progress only if there are references needed
  const hasReferences = job.referencesNeeded > 0;
  const progress = hasReferences ? Math.round((job.referencesCompleted / job.referencesNeeded) * 100) : 0;
  
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'overdue': return 'bg-red-100 text-red-700';
      case 'high': return 'bg-yellow-100 text-yellow-700';
      case 'low': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };
  
  const handleViewDetails = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Add status parameter for completed jobs
    if (job.status === 'COMPLETED' || job.status === 'completed') {
      navigate(`/candidates?jobId=${job.id}&status=COMPLETED`);
    } else {
      navigate(`/candidates?jobId=${job.id}`);
    }
  };

  const isCompleted = job.status === 'COMPLETED' || job.status === 'completed';

  return (
    <div 
      className="bg-white rounded-lg border p-4 hover:shadow-md transition-all cursor-pointer"
      onClick={() => onClick(job)}
    >
      <div className="flex justify-between items-start mb-3">
        <h3 className="font-semibold text-gray-900">{job.title}</h3>
        <span className={`text-xs px-2 py-1 rounded-full font-medium ${getPriorityColor(job.priority)}`}>
          {t(`dashboard.activeJobs.priority.${job.priority}`)}
        </span>
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Building2 className="w-4 h-4" />
          <span>{job.department}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <MapPin className="w-4 h-4" />
          <span>{job.location}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Users className="w-4 h-4" />
          <span>{job.candidates} {t('dashboard.jobCard.candidates')}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Calendar className="w-4 h-4" />
          <span>
            {t('dashboard.jobCard.due')} {new Date(job.dueDate).toLocaleDateString()}
          </span>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">{t('dashboard.jobCard.referenceProgress')}</span>
          <span className="font-medium">
            {job.candidates === 0 ? (
              t('dashboard.jobCard.noCandidates')
            ) : !hasReferences ? (
              t('dashboard.jobCard.pendingReferences')
            ) : (
              `${job.referencesCompleted}/${job.referencesNeeded}`
            )}
          </span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div 
            className="h-full bg-blue-500 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        
        <div className="flex gap-2 mt-4">
          <button
            className="flex-1 flex items-center justify-center gap-1 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 py-2 rounded-lg transition-colors"
            onClick={handleViewDetails}
          >
            {t('common.viewDetails')} <ArrowRight className="w-3.5 h-3.5" />
          </button>
          
          {!isCompleted && onJobCompleted && (
            <CompleteJobButton 
              jobId={(job.id || '').toString()} 
              onCompleted={() => {
                if (onJobCompleted) {
                  onJobCompleted({...job, status: 'COMPLETED'});
                }
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default JobCard;